import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cog6ToothIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Extend Window interface for CSRF token
declare global {
  interface Window {
    CSRF_TOKEN?: string;
    DJANGO_CONFIG?: {
      config?: {
        apiBaseUrl?: string;
      };
    };
  }
}

interface PatientModelField {
  field_name: string;
  field_label: string;
  field_type: string;
  is_core: boolean;
  description: string;
  options?: string[];
}

interface FieldConfiguration {
  is_enabled: boolean;
  is_required: boolean;
  placeholder_text: string;
  help_text: string;
  field_order: number;
}

const PatientSettingsClient: React.FC = () => {
  const { t } = useTranslation(['patients', 'common']);
  
  const [modelFields, setModelFields] = useState<PatientModelField[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<Record<string, FieldConfiguration>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setMessage(null);
      
      // Load model fields directly - the endpoint will handle authentication
      console.log('Loading model fields...');
      const modelResponse = await fetch('/api/patients/settings/model-fields/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': window.CSRF_TOKEN || '',
        }
      });
      console.log('Model fields response status:', modelResponse.status);
      
      if (modelResponse.ok) {
        const responseText = await modelResponse.text();
        console.log('Model fields raw response:', responseText);
        try {
          const modelData = JSON.parse(responseText);
          console.log('Model fields data:', modelData);
          setModelFields(modelData.model_fields || []);
        } catch (parseError) {
          console.error('Failed to parse model fields JSON:', parseError);
          console.error('Response text was:', responseText);
          setMessage({ type: 'error', text: 'Failed to parse model fields response - server may have returned HTML instead of JSON' });
          return;
        }
      } else {
        console.error('Failed to load model fields:', modelResponse.status, modelResponse.statusText);
        setMessage({ type: 'error', text: `Failed to load model fields: ${modelResponse.status}` });
      }

      // Load field configurations
      console.log('Loading field configurations...');
      const configResponse = await fetch('/api/patients/settings/field-config/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': window.CSRF_TOKEN || '',
        }
      });
      console.log('Field config response status:', configResponse.status);
      
      if (configResponse.ok) {
        const responseText = await configResponse.text();
        console.log('Field config raw response:', responseText);
        try {
          const configData = JSON.parse(responseText);
          console.log('Field config data:', configData);
          setFieldConfigs(configData.configured_fields || {});
        } catch (parseError) {
          console.error('Failed to parse field config JSON:', parseError);
          setFieldConfigs({}); // Use empty config as fallback
        }
      } else {
        console.error('Failed to load field config:', configResponse.status, configResponse.statusText);
      }

      // Load form settings
      console.log('Loading form settings...');
      const formResponse = await fetch('/api/patients/settings/form/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': window.CSRF_TOKEN || '',
        }
      });
      console.log('Form settings response status:', formResponse.status);
      
      // Form settings removed - no longer needed
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: `Failed to load settings: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoading(false);
    }
  };

  const updateFieldConfig = (fieldName: string, updates: Partial<FieldConfiguration>) => {
    setFieldConfigs(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        is_enabled: prev[fieldName]?.is_enabled ?? true,
        is_required: prev[fieldName]?.is_required ?? false,
        placeholder_text: prev[fieldName]?.placeholder_text ?? '',
        help_text: prev[fieldName]?.help_text ?? '',
        field_order: prev[fieldName]?.field_order ?? 0,
        ...updates
      }
    }));
  };

  const saveFieldConfigurations = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Prepare field configs for saving
      const configsToSave: Record<string, any> = {};
      
      modelFields.forEach((field, index) => {
        const config = fieldConfigs[field.field_name];
        if (config) {
          configsToSave[field.field_name] = {
            field_label: field.field_label,
            field_type: field.field_type,
            is_enabled: config.is_enabled,
            is_required: field.is_core ? true : config.is_required, // Core fields are always required
            placeholder_text: config.placeholder_text,
            help_text: config.help_text,
            field_order: config.field_order || index,
            field_options: field.options || []
          };
        }
      });

      const response = await fetch('/api/patients/settings/field-config/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': window.CSRF_TOKEN || '',
        },
        credentials: 'include',
        body: JSON.stringify({ field_configs: configsToSave })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Patient form settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (error) {
      console.error('Error saving field configurations:', error);
      setMessage({ type: 'error', text: 'Error saving settings' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl border border-border shadow-sm p-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-xl bg-icon-bg-primary">
            <Cog6ToothIcon className="h-6 w-6 text-icon-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-card-foreground">Patient Settings</h1>
            <p className="text-muted-foreground">Configure patient form fields and behavior for your clinic</p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <XCircleIcon className="h-5 w-5" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Patient Form Fields Configuration */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserIcon className="h-6 w-6 text-icon-primary" />
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Patient Form Fields</h2>
                <p className="text-muted-foreground">Select which fields to include in the patient creation form</p>
              </div>
            </div>
            <button
              onClick={saveFieldConfigurations}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? t('common:saving') : t('settings.saveFieldSettings')}
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {modelFields.map((field, index) => {
              const config = fieldConfigs[field.field_name] || {
                is_enabled: field.is_core, // Core fields enabled by default
                is_required: field.is_core,
                placeholder_text: '',
                help_text: '',
                field_order: index
              };

              return (
                <div
                  key={field.field_name}
                  className={`bg-muted/30 rounded-lg p-4 border transition-all duration-200 ${
                    config.is_enabled ? 'border-primary/30' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Enable/Disable Checkbox */}
                      <div className="flex items-center pt-1">
                        <input
                          type="checkbox"
                          id={`enable-${field.field_name}`}
                          checked={config.is_enabled}
                          disabled={field.is_core} // Core fields cannot be disabled
                          onChange={(e) => updateFieldConfig(field.field_name, { is_enabled: e.target.checked })}
                          className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                        />
                      </div>

                      {/* Field Information */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className={`font-medium ${config.is_enabled ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                            {field.field_label}
                          </h3>
                          <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary">
                            {field.field_type}
                          </span>
                          {field.is_core && (
                            <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-600 flex items-center space-x-1">
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              <span>Core Field</span>
                            </span>
                          )}
                          {config.is_required && (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600">Required</span>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mt-1">
                          Field name: <code className="text-xs bg-muted px-1 py-0.5 rounded">{field.field_name}</code>
                        </p>
                        
                        <p className="text-sm text-muted-foreground mt-1">{field.description}</p>

                        {/* Required Checkbox for Non-Core Fields */}
                        {config.is_enabled && !field.is_core && (
                          <div className="mt-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`required-${field.field_name}`}
                                checked={config.is_required}
                                onChange={(e) => updateFieldConfig(field.field_name, { is_required: e.target.checked })}
                                className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                              />
                              <label htmlFor={`required-${field.field_name}`} className="ml-2 block text-sm text-card-foreground">
                                Make this field required
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {modelFields.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-card-foreground mb-2">No model fields found</h3>
              <p className="text-muted-foreground">Unable to load patient model fields</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default PatientSettingsClient;
